import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {Search, MapPin, Calendar, Clock, ArrowRight, Download, Printer, Settings, ChevronDown, Star, Globe, Moon as MoonIcon, Sun as SunIcon, Sparkles, Info, RefreshCw} from 'lucide-react';
import { lumi } from '../lib/lumi';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

export function AdvancedCalculator() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [formData, setFormData] = useState({
    birthDate: '',
    birthTime: '',
    birthLocation: '',
    houseSystem: 'placidus',
    zodiacType: 'tropical',
    aspectOrb: '8'
  });

  const handleCalculate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // First, geocode the location
      const geoResponse = await lumi.functions.invoke('geocodeLocation', {
        method: 'POST',
        body: { location: formData.birthLocation }
      });

      if (!geoResponse.success) {
        toast.error(geoResponse.error || 'Location not found. Please try a different format (e.g., "New York, USA").');
        setLoading(false);
        return;
      }

      const { latitude, longitude, timezoneOffset } = geoResponse.data;

      // Calculate Rising Sign using real ephemeris data
      const chartResponse = await lumi.functions.invoke('calculateRisingSign', {
        method: 'POST',
        body: {
          birthDate: formData.birthDate,
          birthTime: formData.birthTime,
          latitude,
          longitude,
          timezone: timezoneOffset,
          houseSystem: formData.houseSystem,
          zodiacType: formData.zodiacType
        }
      });

      if (chartResponse.success) {
        setResult(chartResponse.data);
        // Save to localStorage for Daily Horoscope feature
        localStorage.setItem('userBig3', JSON.stringify({
          sun: chartResponse.data.sun,
          moon: chartResponse.data.moon,
          rising: chartResponse.data.rising,
          birthDate: formData.birthDate,
          birthTime: formData.birthTime,
          birthLocation: formData.birthLocation,
          fullChart: chartResponse.data
        }));
        setStep(2);
        toast.success('🌟 Birth chart calculated successfully!');
      } else {
        toast.error(chartResponse.error || 'Failed to calculate birth chart. Please check your input.');
      }
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('An error occurred. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    const content = generatePDFContent();
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `birth-chart-${formData.birthDate}.html`;
    a.click();
    toast.success('📄 Chart downloaded! Open the HTML file in your browser to print as PDF.');
  };

  const handlePrint = () => {
    window.print();
    toast.success('🖨️ Opening print dialog...');
  };

  const generatePDFContent = () => {
    return `
<!DOCTYPE html>
<html>
<head>
  <title>Birth Chart - ${formData.birthLocation}</title>
  <style>
    body { font-family: 'Georgia', serif; max-width: 900px; margin: 40px auto; padding: 30px; background: #fff; color: #333; }
    h1 { color: #D4AF37; text-align: center; font-size: 2.5em; margin-bottom: 10px; }
    .subtitle { text-align: center; color: #666; margin-bottom: 30px; font-size: 1.1em; }
    .info-box { background: #f9f9f9; padding: 20px; border-left: 4px solid #D4AF37; margin: 20px 0; }
    .big-three { display: flex; justify-content: space-around; margin: 40px 0; gap: 20px; }
    .sign-card { text-align: center; padding: 30px; border: 3px solid #D4AF37; border-radius: 15px; flex: 1; background: linear-gradient(135deg, #f5f5f5 0%, #fff 100%); }
    .sign-card h2 { color: #D4AF37; font-size: 1.2em; margin-bottom: 10px; }
    .sign-card h3 { font-size: 2em; color: #333; margin: 10px 0; }
    .sign-card p { color: #666; font-size: 0.9em; }
    .planets-table { width: 100%; border-collapse: collapse; margin: 30px 0; }
    .planets-table th, .planets-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
    .planets-table th { background: #D4AF37; color: white; font-weight: bold; }
    .planets-table tr:nth-child(even) { background: #f9f9f9; }
    .section { margin: 40px 0; page-break-inside: avoid; }
    .section h2 { color: #D4AF37; border-bottom: 2px solid #D4AF37; padding-bottom: 10px; margin-bottom: 20px; }
    .houses-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }
    .house-card { background: #f9f9f9; padding: 15px; border-radius: 8px; text-align: center; border: 1px solid #ddd; }
    .house-card .house-num { color: #D4AF37; font-weight: bold; font-size: 0.9em; }
    .house-card .house-sign { font-size: 1.2em; color: #333; margin-top: 5px; }
    .aspect-list { margin: 20px 0; }
    .aspect-item { padding: 12px; margin: 8px 0; background: #f9f9f9; border-left: 3px solid #D4AF37; }
    .pattern-box { background: linear-gradient(135deg, #f0e6ff 0%, #fff 100%); padding: 20px; border-radius: 10px; margin: 15px 0; border: 2px solid #D4AF37; }
    .pattern-box h3 { color: #D4AF37; margin-bottom: 10px; }
    footer { margin-top: 60px; text-align: center; color: #999; font-size: 0.9em; border-top: 1px solid #ddd; padding-top: 20px; }
    @media print { 
      body { margin: 0; padding: 20px; } 
      .big-three { page-break-after: avoid; }
      .section { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>🌟 Natal Birth Chart Analysis 🌟</h1>
  <p class="subtitle">Complete Astrological Profile</p>
  
  <div class="info-box">
    <p><strong>Birth Date:</strong> ${formData.birthDate}</p>
    <p><strong>Birth Time:</strong> ${formData.birthTime}</p>
    <p><strong>Birth Location:</strong> ${formData.birthLocation}</p>
    <p><strong>House System:</strong> ${formData.houseSystem.charAt(0).toUpperCase() + formData.houseSystem.slice(1)}</p>
    <p><strong>Zodiac Type:</strong> ${formData.zodiacType.charAt(0).toUpperCase() + formData.zodiacType.slice(1)}</p>
  </div>
  
  <div class="section">
    <h2>✨ The Big Three - Your Core Identity</h2>
    <div class="big-three">
      <div class="sign-card">
        <h2>☉ Sun Sign</h2>
        <h3>${result.sun}</h3>
        <p>Your Ego & Core Identity</p>
      </div>
      <div class="sign-card">
        <h2>☾ Moon Sign</h2>
        <h3>${result.moon}</h3>
        <p>Your Emotions & Inner Self</p>
      </div>
      <div class="sign-card">
        <h2>↑ Rising Sign (Ascendant)</h2>
        <h3>${result.rising}</h3>
        <p>Your Persona & First Impression</p>
      </div>
    </div>
  </div>
  
  <div class="section">
    <h2>📖 Chart Description</h2>
    <p style="line-height: 1.8; color: #555;">${result.description}</p>
  </div>
  
  <div class="section">
    <h2>🪐 Planetary Positions</h2>
    <table class="planets-table">
      <tr>
        <th>Planet</th>
        <th>Sign</th>
        <th>Degree</th>
        <th>House</th>
      </tr>
      ${result.planetaryPositions ? Object.entries(result.planetaryPositions).map(([planet, data]: any) => `
        <tr>
          <td><strong>${planet}</strong></td>
          <td>${data.sign || 'N/A'}</td>
          <td>${data.degree ? data.degree.toFixed(2) + '°' : 'N/A'}</td>
          <td>${data.house || 'N/A'}</td>
        </tr>
      `).join('') : '<tr><td colspan="4">No data available</td></tr>'}
    </table>
  </div>
  
  ${result.houses ? `
  <div class="section">
    <h2>🏠 House Cusps (${formData.houseSystem.charAt(0).toUpperCase() + formData.houseSystem.slice(1)} System)</h2>
    <div class="houses-grid">
      ${Object.entries(result.houses).map(([house, sign]: any) => `
        <div class="house-card">
          <div class="house-num">${house}</div>
          <div class="house-sign">${sign}</div>
        </div>
      `).join('')}
    </div>
  </div>
  ` : ''}
  
  ${result.aspects && result.aspects.length > 0 ? `
  <div class="section">
    <h2>✨ Major Aspects</h2>
    <div class="aspect-list">
      ${result.aspects.slice(0, 15).map((aspect: any) => `
        <div class="aspect-item">
          <strong>${aspect.planet1}</strong> ${getAspectSymbol(aspect.type)} <strong>${aspect.planet2}</strong> 
          <span style="float: right; color: #D4AF37;">${aspect.orb}° orb</span>
        </div>
      `).join('')}
    </div>
  </div>
  ` : ''}
  
  ${result.chartPatterns && result.chartPatterns.length > 0 ? `
  <div class="section">
    <h2>🌟 Special Chart Patterns</h2>
    ${result.chartPatterns.map((pattern: any) => `
      <div class="pattern-box">
        <h3>${pattern.name}</h3>
        <p style="color: #555; line-height: 1.6;">${pattern.description}</p>
      </div>
    `).join('')}
  </div>
  ` : ''}
  
  <footer>
    <p>Generated by <strong>AscendedCalculators.com</strong></p>
    <p>Rising Sign Astro - Professional Birth Chart Analysis</p>
    <p style="margin-top: 10px; font-size: 0.85em;">This chart uses Swiss Ephemeris for maximum astronomical accuracy</p>
  </footer>
</body>
</html>
    `;
  };

  return (
    <div className="w-full max-w-7xl mx-auto">
      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-[#131722] border border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-black/50"
          >
            <div className="p-1 bg-gradient-to-r from-[#D4AF37] via-purple-500 to-[#D4AF37] opacity-30" />
            
            <div className="p-8 md:p-12">
              <div className="text-center mb-10">
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="inline-block mb-4"
                >
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <Sparkles className="w-8 h-8 text-[#D4AF37] animate-pulse" />
                    <h2 className="text-3xl md:text-5xl font-serif font-bold text-white">
                      Advanced Rising Sign Calculator
                    </h2>
                    <Sparkles className="w-8 h-8 text-[#D4AF37] animate-pulse" />
                  </div>
                </motion.div>
                <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
                  Professional-grade astronomical calculator using <strong className="text-[#D4AF37]">Swiss Ephemeris</strong> data. 
                  Discover your complete birth chart with planetary positions, houses, aspects, and rare chart patterns.
                </p>
                
                <button
                  onClick={() => setShowHelp(!showHelp)}
                  className="mt-4 flex items-center gap-2 mx-auto text-sm text-gray-500 hover:text-[#D4AF37] transition-colors"
                >
                  <Info className="w-4 h-4" />
                  {showHelp ? 'Hide Help' : 'Why do I need exact birth time?'}
                </button>

                {showHelp && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg text-sm text-gray-300"
                  >
                    <p className="mb-2">
                      <strong className="text-[#D4AF37]">Your Rising Sign changes every 2 hours</strong>, making it the most personal point in your chart.
                    </p>
                    <p>
                      The exact time determines house placements, planetary positions, and aspects. 
                      Even a few minutes can shift your Ascendant degree and affect interpretations.
                    </p>
                  </motion.div>
                )}
              </div>

              <form onSubmit={handleCalculate} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-[#D4AF37] flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Birth Date *
                    </label>
                    <input 
                      type="date" 
                      required
                      value={formData.birthDate}
                      onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                      className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all outline-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-[#D4AF37] flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Birth Time *
                    </label>
                    <input 
                      type="time" 
                      required
                      value={formData.birthTime}
                      onChange={(e) => setFormData({...formData, birthTime: e.target.value})}
                      className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all outline-none"
                    />
                    <p className="text-xs text-gray-500">Exact time is crucial for accuracy</p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-[#D4AF37] flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Birth Location *
                    </label>
                    <input 
                      type="text" 
                      placeholder="City, Country (e.g., Paris, France)"
                      required
                      value={formData.birthLocation}
                      onChange={(e) => setFormData({...formData, birthLocation: e.target.value})}
                      className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all outline-none"
                    />
                  </div>
                </div>

                {/* Advanced Options */}
                <div className="border-t border-white/10 pt-6">
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors text-sm font-medium group"
                  >
                    <Settings className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                    Advanced Calculation Options
                    <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                  </button>

                  {showAdvanced && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6"
                    >
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-400 flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          House System
                        </label>
                        <select
                          value={formData.houseSystem}
                          onChange={(e) => setFormData({...formData, houseSystem: e.target.value})}
                          className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all outline-none"
                        >
                          <option value="placidus">Placidus (Most Popular)</option>
                          <option value="koch">Koch</option>
                          <option value="whole-sign">Whole Sign (Ancient)</option>
                          <option value="equal">Equal House</option>
                          <option value="campanus">Campanus</option>
                          <option value="regiomontanus">Regiomontanus</option>
                          <option value="porphyry">Porphyry</option>
                        </select>
                        <p className="text-xs text-gray-600">Affects house cusp positions</p>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-400 flex items-center gap-2">
                          <SunIcon className="w-4 h-4" />
                          Zodiac Type
                        </label>
                        <select
                          value={formData.zodiacType}
                          onChange={(e) => setFormData({...formData, zodiacType: e.target.value})}
                          className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all outline-none"
                        >
                          <option value="tropical">Tropical (Western Astrology)</option>
                          <option value="sidereal">Sidereal (Vedic Astrology)</option>
                        </select>
                        <p className="text-xs text-gray-600">~24° difference between systems</p>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-400 flex items-center gap-2">
                          <Sparkles className="w-4 h-4" />
                          Aspect Orb
                        </label>
                        <select
                          value={formData.aspectOrb}
                          onChange={(e) => setFormData({...formData, aspectOrb: e.target.value})}
                          className="w-full bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-all outline-none"
                        >
                          <option value="6">Tight (±6°)</option>
                          <option value="8">Standard (±8°)</option>
                          <option value="10">Wide (±10°)</option>
                        </select>
                        <p className="text-xs text-gray-600">Tolerance for aspect calculations</p>
                      </div>
                    </motion.div>
                  )}
                </div>

                <div className="flex justify-center pt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative px-10 py-4 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0B0E14] font-bold rounded-xl overflow-hidden transition-all active:scale-95 hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
                  >
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    <span className="relative flex items-center gap-3 text-lg">
                      {loading ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Calculating Celestial Positions...
                        </>
                      ) : (
                        <>
                          <Star className="w-5 h-5" />
                          Calculate My Birth Chart
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-4 print:hidden">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0B0E14] font-medium rounded-lg hover:bg-[#C4A037] transition-all hover:scale-105"
              >
                <Download className="w-4 h-4" />
                Download Chart
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-medium rounded-lg hover:bg-white/20 transition-all hover:scale-105"
              >
                <Printer className="w-4 h-4" />
                Print Chart
              </button>
              <Link
                to="/daily-horoscope"
                className="flex items-center gap-2 px-6 py-3 bg-purple-600/20 border border-purple-500/50 text-purple-300 font-medium rounded-lg hover:bg-purple-600/30 transition-all hover:scale-105"
              >
                <Star className="w-4 h-4" />
                View Daily Horoscope
              </Link>
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600/20 border border-blue-500/50 text-blue-300 font-medium rounded-lg hover:bg-blue-600/30 transition-all hover:scale-105"
              >
                <Search className="w-4 h-4" />
                New Chart
              </button>
            </div>

            {/* Big 3 Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ResultCard 
                title="Sun Sign" 
                value={result.sun} 
                icon={<SunIcon className="w-12 h-12" />}
                color="text-orange-400" 
                description="Your ego, identity, and core self"
                bgGradient="from-orange-500/10 to-yellow-500/10"
              />
              <ResultCard 
                title="Moon Sign" 
                value={result.moon} 
                icon={<MoonIcon className="w-12 h-12" />}
                color="text-blue-300" 
                description="Your emotions and inner world"
                bgGradient="from-blue-500/10 to-purple-500/10"
              />
              <ResultCard 
                title="Rising Sign (Ascendant)" 
                value={result.rising} 
                icon={<Sparkles className="w-12 h-12" />}
                color="text-[#D4AF37]" 
                highlight 
                description="Your persona and first impression"
                bgGradient="from-[#D4AF37]/20 to-yellow-500/20"
              />
            </div>
            
            {/* Chart Description */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-[#131722] to-[#0B0E14] p-8 rounded-xl border border-white/10"
            >
              <h3 className="text-2xl font-serif text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                Your Celestial Snapshot
              </h3>
              <p className="text-gray-300 leading-relaxed text-lg">{result.description}</p>
            </motion.div>

            {/* Planetary Positions */}
            {result.planetaryPositions && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="bg-[#131722] p-8 rounded-xl border border-white/10"
              >
                <h3 className="text-2xl font-serif text-white mb-6 flex items-center gap-2">
                  🪐 Planetary Positions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(result.planetaryPositions).map(([planet, data]: any) => (
                    <div key={planet} className="flex justify-between items-center bg-white/5 p-4 rounded-lg hover:bg-white/10 transition-colors">
                      <span className="text-gray-300 font-medium">{planet}</span>
                      <span className="text-white font-semibold">
                        {data.sign || 'N/A'} {data.degree ? `${data.degree.toFixed(1)}°` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Houses */}
            {result.houses && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="bg-[#131722] p-8 rounded-xl border border-white/10"
              >
                <h3 className="text-2xl font-serif text-white mb-6 flex items-center gap-2">
                  🏠 House Cusps ({formData.houseSystem.charAt(0).toUpperCase() + formData.houseSystem.slice(1)})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(result.houses).map(([house, sign]: any) => (
                    <div key={house} className="bg-gradient-to-br from-white/5 to-white/10 p-4 rounded-lg text-center hover:scale-105 transition-transform">
                      <div className="text-xs text-[#D4AF37] font-bold mb-2">{house}</div>
                      <div className="text-white font-semibold text-lg">{sign}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Aspects */}
            {result.aspects && result.aspects.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="bg-[#131722] p-8 rounded-xl border border-white/10"
              >
                <h3 className="text-2xl font-serif text-white mb-6 flex items-center gap-2">
                  ✨ Major Aspects
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {result.aspects.slice(0, 20).map((aspect: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center bg-white/5 p-4 rounded-lg hover:bg-white/10 transition-colors">
                      <span className="text-gray-300">
                        <strong className="text-white">{aspect.planet1}</strong> {getAspectSymbol(aspect.type)} <strong className="text-white">{aspect.planet2}</strong>
                      </span>
                      <span className="text-[#D4AF37] text-sm font-semibold">{aspect.orb}° orb</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Chart Patterns */}
            {result.chartPatterns && result.chartPatterns.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 p-8 rounded-xl border border-purple-500/30"
              >
                <h3 className="text-2xl font-serif text-white mb-6 flex items-center gap-2">
                  🌟 Special Chart Patterns
                </h3>
                <div className="space-y-4">
                  {result.chartPatterns.map((pattern: any, idx: number) => (
                    <div key={idx} className="bg-black/30 p-6 rounded-lg border border-purple-500/20">
                      <div className="text-[#D4AF37] font-bold text-xl mb-3">{pattern.name}</div>
                      <p className="text-gray-300 leading-relaxed">{pattern.description}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ResultCard({ title, value, icon, color, highlight = false, description, bgGradient }: { 
  title: string, 
  value: string, 
  icon: React.ReactNode,
  color: string, 
  highlight?: boolean,
  description?: string,
  bgGradient?: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-8 rounded-xl border ${highlight ? `bg-gradient-to-br ${bgGradient} border-[#D4AF37]/50 shadow-[0_0_30px_rgba(212,175,55,0.3)]` : `bg-gradient-to-br ${bgGradient} border-white/10`} text-center group hover:scale-105 transition-all duration-300`}
    >
      <div className={`mb-4 ${color} flex justify-center`}>{icon}</div>
      <div className="text-sm text-gray-400 uppercase tracking-wider font-medium mb-2">{title}</div>
      <div className="text-3xl font-serif font-bold text-white mb-2">{value}</div>
      {description && <div className="text-xs text-gray-500 leading-relaxed">{description}</div>}
      {highlight && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-[#0B0E14] text-xs font-bold rounded-full shadow-lg">
          Your Ascendant ✨
        </div>
      )}
    </motion.div>
  );
}

function getAspectSymbol(type: string): string {
  const symbols: Record<string, string> = {
    conjunction: '☌',
    opposition: '☍',
    trine: '△',
    square: '□',
    sextile: '⚹',
    quincunx: '⚻',
    semisextile: '⚺'
  };
  return symbols[type.toLowerCase()] || type;
}
